import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Plus, Search, PanelLeftClose, PanelLeftOpen, Pencil, Trash2, MessageSquare, Check, X,
} from 'lucide-react';

export type ConversationSummary = {
  id: string; title: string; createdAt: string; updatedAt: string; turnCount: number;
};

type Props = {
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  /** Bumped by the parent after a turn completes so the rail re-fetches. */
  refreshKey: number;
  /** Called when the open conversation is deleted, so the parent can reset. */
  onDeleted: (id: string) => void;
};

const COLLAPSE_KEY = 'nam-ai:rail-collapsed';

// Claude-style date buckets, newest first.
const BUCKETS = ['Today', 'Yesterday', 'Previous 7 days', 'Previous 30 days', 'Older'] as const;
const bucketOf = (iso: string): typeof BUCKETS[number] => {
  const midnight = new Date(); midnight.setHours(0, 0, 0, 0);
  const days = Math.floor((midnight.getTime() - new Date(iso).getTime()) / 86400000);
  if (days < 0) return 'Today';
  if (days < 1) return 'Yesterday';
  if (days < 7) return 'Previous 7 days';
  if (days < 30) return 'Previous 30 days';
  return 'Older';
};

const ConversationRail: React.FC<Props> = ({ activeId, onSelect, onNew, refreshKey, onDeleted }) => {
  const [items, setItems] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const editRef = useRef<HTMLInputElement>(null);

  useEffect(() => { const t = setTimeout(() => setDebouncedQ(q), 250); return () => clearTimeout(t); }, [q]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get('/api/my/conversations', { params: debouncedQ.trim() ? { q: debouncedQ.trim() } : {} })
      .then(r => { if (!cancelled) setItems(r.data); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedQ, refreshKey]);

  useEffect(() => { if (editingId) editRef.current?.focus(); }, [editingId]);

  const toggle = () => setCollapsed(c => {
    const next = !c;
    try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch { /* private mode */ }
    return next;
  });

  const startRename = (c: ConversationSummary) => { setEditingId(c.id); setDraft(c.title); };
  const cancelRename = () => { setEditingId(null); setDraft(''); };
  const commitRename = async (id: string) => {
    const title = draft.trim();
    if (!title) return cancelRename();
    setItems(prev => prev.map(c => (c.id === id ? { ...c, title } : c)));  // optimistic
    cancelRename();
    try { await axios.patch(`/api/my/conversations/${id}`, { title }); }
    catch { setItems(prev => [...prev]); }
  };

  const remove = async (c: ConversationSummary) => {
    if (!window.confirm(`Delete "${c.title}"? This can't be undone.`)) return;
    setItems(prev => prev.filter(x => x.id !== c.id));
    try { await axios.delete(`/api/my/conversations/${c.id}`); } catch { /* refreshKey will resync */ }
    onDeleted(c.id);
  };

  if (collapsed) {
    return (
      <div className="w-[52px] shrink-0 border-r border-[var(--glass-border)] bg-[var(--glass-bg)] backdrop-blur-[16px] flex flex-col items-center gap-2 py-3">
        <button
          onClick={toggle}
          title="Show conversations"
          className="w-9 h-9 rounded-[10px] flex items-center justify-center text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] hover:bg-[var(--surface-hover)] bg-transparent border-none cursor-pointer transition-colors"
        >
          <PanelLeftOpen size={16} />
        </button>
        <button
          onClick={onNew}
          title="New chat"
          className="w-9 h-9 rounded-[10px] flex items-center justify-center text-[color:var(--accent)] bg-[var(--accent-dim)] border border-[var(--accent-border)] cursor-pointer hover:opacity-90 transition-opacity"
        >
          <Plus size={16} />
        </button>
      </div>
    );
  }

  const groups = BUCKETS
    .map(b => ({ bucket: b, rows: items.filter(c => bucketOf(c.updatedAt) === b) }))
    .filter(g => g.rows.length > 0);

  return (
    <div className="w-[268px] shrink-0 border-r border-[var(--glass-border)] bg-[var(--glass-bg)] backdrop-blur-[16px] flex flex-col h-full">
      {/* Header */}
      <div className="p-3 pb-2 shrink-0">
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={onNew}
            className="flex-1 flex items-center gap-2 px-3 py-[9px] rounded-[10px] bg-[var(--accent)] text-white text-[13px] font-medium border-none cursor-pointer hover:opacity-90 transition-opacity"
          >
            <Plus size={15} /> New chat
          </button>
          <button
            onClick={toggle}
            title="Hide conversations"
            className="w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0 text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] hover:bg-[var(--surface-hover)] bg-transparent border-none cursor-pointer transition-colors"
          >
            <PanelLeftClose size={16} />
          </button>
        </div>
        <div className="relative">
          <Search size={13} className="absolute left-[10px] top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]" />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search chats"
            className="w-full bg-[var(--surface)] border border-[var(--border)] rounded-[9px] pl-[30px] pr-2 py-[7px] text-[12px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] outline-none focus:border-[var(--accent)] transition-colors"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-2 pb-3">
        {loading && items.length === 0 && (
          <div className="text-[12px] text-[color:var(--text-muted)] px-2 py-3">Loading…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="px-2 py-6 text-center">
            <MessageSquare size={20} className="mx-auto mb-2 text-[color:var(--text-muted)]" />
            <p className="text-[12px] text-[color:var(--text-muted)] m-0 leading-[1.5]">
              {debouncedQ ? 'No chats match that search.' : 'Your conversations will appear here.'}
            </p>
          </div>
        )}

        {groups.map(({ bucket, rows }) => (
          <div key={bucket} className="mb-3">
            <div className="text-[10px] uppercase tracking-wide text-[color:var(--text-muted)] px-2 py-1">{bucket}</div>
            <div className="flex flex-col gap-[1px]">
              {rows.map(c => {
                const active = c.id === activeId;
                if (editingId === c.id) {
                  return (
                    <div key={c.id} className="flex items-center gap-1 px-2 py-1">
                      <input
                        ref={editRef}
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') commitRename(c.id);
                          if (e.key === 'Escape') cancelRename();
                        }}
                        className="flex-1 min-w-0 bg-[var(--surface)] border border-[var(--accent-border)] rounded-[7px] px-2 py-[5px] text-[12px] text-[color:var(--text-primary)] outline-none"
                      />
                      <button onClick={() => commitRename(c.id)} title="Save" className="p-1 text-[color:var(--accent)] bg-transparent border-none cursor-pointer"><Check size={13} /></button>
                      <button onClick={cancelRename} title="Cancel" className="p-1 text-[color:var(--text-muted)] bg-transparent border-none cursor-pointer"><X size={13} /></button>
                    </div>
                  );
                }
                return (
                  <div
                    key={c.id}
                    className={`group relative flex items-center rounded-[9px] transition-colors ${active ? 'bg-[var(--accent-dim)]' : 'hover:bg-[var(--surface-hover)]'}`}
                  >
                    <button
                      onClick={() => onSelect(c.id)}
                      title={c.title}
                      className="flex-1 min-w-0 text-left px-[10px] py-[7px] bg-transparent border-none cursor-pointer"
                    >
                      <div className={`text-[12.5px] truncate ${active ? 'text-[color:var(--accent-strong)]' : 'text-[color:var(--text-secondary)]'}`}>
                        {c.title}
                      </div>
                    </button>
                    {/* Row actions — revealed on hover/focus, like Claude's kebab */}
                    <div className="flex items-center gap-[2px] pr-[6px] opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                      <button
                        onClick={() => startRename(c)}
                        title="Rename"
                        className="p-1 rounded-md text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] bg-transparent border-none cursor-pointer"
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        onClick={() => remove(c)}
                        title="Delete"
                        className="p-1 rounded-md text-[color:var(--text-muted)] hover:text-[color:var(--danger-strong)] bg-transparent border-none cursor-pointer"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ConversationRail;
